import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { COMM_TEMPLATE_TYPES, SMART_SEGMENTS, AUTOMATION_TRIGGERS, PARTNER_CATEGORIES, commTemplateEntry, automationTriggerEntry, pl } from "../../lib/partnerConstants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Megaphone, Plus, Send, Trash2, Mail, FileText, Users, MousePointerClick, XCircle, Settings, Eye, Filter, Zap, Play, Clock, CalendarClock } from "lucide-react";
import RichTextEditor from "./RichTextEditor";

const emptyCampaign = {
  name: "", template_id: "", subject: "", body_html: "", cta_label: "", cta_url: "",
  segment: { categories: [], communes: [], maison_ids: [], score_min: null, satisfaction_min: null,
    satisfaction_max: null, inactive_days_min: null, orientations_min: null, conversion_min: null, smart: null, include_optout: false },
};
const numN = (v) => (v === "" || v == null ? null : Number(v));

export default function CommunicationTab({ readOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [totals, setTotals] = useState(null);
  const [openTracking, setOpenTracking] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCampaign);
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [tpl, setTpl] = useState({ name: "", template_type: "newsletter", subject: "", body_html: "" });
  const [tplEditId, setTplEditId] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [schedAt, setSchedAt] = useState("");
  const [autoOpen, setAutoOpen] = useState(false);
  const [autos, setAutos] = useState([]);
  const [autoForm, setAutoForm] = useState({ name: "", trigger_type: "relance_inactivity", active: true, subject: "", body_html: "", cta_label: "", cta_url: "", config: {} });
  const [autoEditId, setAutoEditId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, st] = await Promise.all([api.get("/crm/campaigns"), api.get("/crm/campaigns/stats")]);
      setCampaigns(c.data.campaigns || []); setTotals(st.data.totals);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [tp, mm, cs] = await Promise.all([api.get("/crm/templates"), api.get("/my/maisons"), api.get("/crm/comm-settings")]);
        setTemplates(tp.data.templates || []); setMaisons(mm.data.maisons || []); setOpenTracking(!!cs.data.open_tracking_enabled);
      } catch { /* non bloquant */ }
    })();
  }, []);

  const doPreview = useCallback(async (seg) => {
    try { const r = await api.post("/crm/segments/preview", seg); setPreview(r.data); }
    catch { setPreview(null); }
  }, []);
  useEffect(() => { if (open) doPreview(form.segment); }, [open, form.segment, doPreview]);

  const openNew = () => { setForm(JSON.parse(JSON.stringify(emptyCampaign))); setEditId(null); setPreview(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ ...emptyCampaign, ...c, segment: { ...emptyCampaign.segment, ...(c.segment || {}) } });
    setEditId(c.id); setOpen(true);
  };
  const applyTemplate = (tid) => {
    const tpl = templates.find((x) => x.id === tid);
    setForm((f) => ({ ...f, template_id: tid, subject: tpl?.subject || f.subject, body_html: tpl?.body_html || f.body_html }));
  };

  const buildPayload = () => ({
    ...form,
    segment: { ...form.segment,
      score_min: numN(form.segment.score_min), satisfaction_min: numN(form.segment.satisfaction_min),
      satisfaction_max: numN(form.segment.satisfaction_max), inactive_days_min: numN(form.segment.inactive_days_min),
      orientations_min: numN(form.segment.orientations_min), conversion_min: numN(form.segment.conversion_min) },
  });

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("crm.comm.nameRequired"));
    try {
      const payload = buildPayload();
      if (editId) { await api.put(`/crm/campaigns/${editId}`, payload); }
      else { const r = await api.post("/crm/campaigns", payload); setEditId(r.data.id); }
      toast.success(t("crm.comm.saved")); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(t("crm.comm.confirmDelete"))) return;
    try { await api.delete(`/crm/campaigns/${c.id}`); toast.success(t("crm.comm.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendTest = async () => {
    if (!editId) return toast.error(t("crm.comm.saveFirst"));
    if (!testEmail.trim()) return toast.error(t("crm.comm.testEmailRequired"));
    try { await api.post(`/crm/campaigns/${editId}/send`, { base_url: window.location.origin, test_email: testEmail.trim() }); toast.success(t("crm.comm.testSent")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendNow = async () => {
    if (!editId) { await save(); return toast.info(t("crm.comm.savedSendAgain")); }
    if (!window.confirm(t("crm.comm.confirmSend", { count: preview?.eligible ?? "?" }))) return;
    try {
      await api.put(`/crm/campaigns/${editId}`, buildPayload());
      const r = await api.post(`/crm/campaigns/${editId}/send`, { base_url: window.location.origin });
      toast.success(t("crm.comm.sentOk", { count: r.data.stats?.delivered ?? 0 }));
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleCat = (k) => setForm((f) => {
    const cur = f.segment.categories || [];
    return { ...f, segment: { ...f.segment, categories: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] } };
  });
  const setSeg = (patch) => setForm((f) => ({ ...f, segment: { ...f.segment, ...patch } }));

  const saveTracking = async (v) => {
    setOpenTracking(v);
    try { await api.put("/crm/comm-settings", { open_tracking_enabled: v }); toast.success(t("crm.comm.settingsSaved")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setOpenTracking(!v); }
  };

  // Templates CRUD
  const openTplNew = () => { setTpl({ name: "", template_type: "newsletter", subject: "", body_html: "" }); setTplEditId(null); setTplOpen(true); };
  const openTplEdit = (x) => { setTpl({ ...x }); setTplEditId(x.id); setTplOpen(true); };
  const saveTpl = async () => {
    if (!tpl.name.trim()) return toast.error(t("crm.comm.tplNameRequired"));
    try {
      if (tplEditId) await api.put(`/crm/templates/${tplEditId}`, tpl); else await api.post("/crm/templates", tpl);
      toast.success(t("crm.comm.tplSaved")); setTplOpen(false);
      const r = await api.get("/crm/templates"); setTemplates(r.data.templates || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTpl = async (x) => {
    try { await api.delete(`/crm/templates/${x.id}`); const r = await api.get("/crm/templates"); setTemplates(r.data.templates || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Scheduling
  const schedule = async () => {
    if (!editId) return toast.error(t("crm.comm.saveFirst"));
    if (!schedAt) return toast.error(t("crm.comm.schedDateRequired"));
    try {
      await api.put(`/crm/campaigns/${editId}`, buildPayload());
      await api.post(`/crm/campaigns/${editId}/schedule`, { scheduled_at: new Date(schedAt).toISOString(), base_url: window.location.origin });
      toast.success(t("crm.comm.scheduled")); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Automations
  const loadAutos = async () => { try { const r = await api.get("/crm/automations"); setAutos(r.data.automations || []); } catch { /* */ } };
  const openAutos = () => { loadAutos(); setAutoForm({ name: "", trigger_type: "relance_inactivity", active: true, subject: "", body_html: "", cta_label: "", cta_url: "", config: {} }); setAutoEditId(null); setAutoOpen(true); };
  const editAuto = (a) => { setAutoForm({ ...a, config: a.config || {} }); setAutoEditId(a.id); };
  const setCfg = (patch) => setAutoForm((f) => ({ ...f, config: { ...f.config, ...patch } }));
  const saveAuto = async () => {
    if (!autoForm.name.trim()) return toast.error(t("crm.comm.auto.nameRequired"));
    if (!autoForm.subject.trim() || !autoForm.body_html.trim()) return toast.error(t("crm.comm.auto.contentRequired"));
    try {
      if (autoEditId) await api.put(`/crm/automations/${autoEditId}`, autoForm); else await api.post("/crm/automations", autoForm);
      toast.success(t("crm.comm.auto.saved")); loadAutos(); setAutoEditId(null);
      setAutoForm({ name: "", trigger_type: "relance_inactivity", active: true, subject: "", body_html: "", cta_label: "", cta_url: "", config: {} });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAuto = async (a) => {
    try { await api.put(`/crm/automations/${a.id}`, { ...a, active: !a.active }); loadAutos(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runAuto = async (a) => {
    try { const r = await api.post(`/crm/automations/${a.id}/run`, { base_url: window.location.origin }); toast.success(t("crm.comm.auto.ran", { count: r.data.sent })); loadAutos(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delAuto = async (a) => {
    if (!window.confirm(t("crm.comm.auto.confirmDelete"))) return;
    try { await api.delete(`/crm/automations/${a.id}`); loadAutos(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusColor = { brouillon: "#94a3b8", programmee: "#3b82f6", envoyee: "#10b981" };

  return (
    <div className="space-y-4" data-testid="crm-communication-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-emerald-400" /> {t("crm.comm.title")}</h3>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs rounded-xl border border-white/10 px-3 py-1.5" title={t("crm.comm.openTrackingHint")}>
            <Settings className="h-3.5 w-3.5 text-muted-foreground" /> {t("crm.comm.openTracking")}
            <Switch checked={openTracking} onCheckedChange={saveTracking} disabled={readOnly} data-testid="comm-open-tracking" />
          </label>
          <Button variant="outline" size="sm" onClick={openTplNew} data-testid="comm-templates-btn"><FileText className="h-4 w-4 mr-1" /> {t("crm.comm.templates")}</Button>
          {!readOnly && <Button variant="outline" size="sm" onClick={openAutos} data-testid="comm-automations-btn"><Zap className="h-4 w-4 mr-1" /> {t("crm.comm.auto.title")}</Button>}
          {!readOnly && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500" size="sm" data-testid="comm-add-campaign"><Plus className="h-4 w-4 mr-1" /> {t("crm.comm.addBtn")}</Button>}
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3" data-testid="comm-totals">
          {[["campaigns", totals.campaigns, "#60a5fa"], ["sent", totals.sent, "#34d399"], ["delivered", totals.delivered, "#22d3ee"],
            ["failed", totals.failed, "#f87171"], ["clicks", totals.clicks_unique, "#a78bfa"], ["unsub", totals.unsubscribed, "#fb923c"]].map(([k, v, c]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[11px] text-muted-foreground">{t(`crm.comm.stat.${k}`)}</div>
              <div className="font-display text-xl font-bold" style={{ color: c }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {campaigns.map((c) => {
          const s = c.stats || {};
          return (
            <div key={c.id} className="rounded-2xl border border-white/10 p-4 bg-white/[0.02] hover:border-emerald-400/30 transition cursor-pointer" data-testid={`comm-campaign-${c.id}`} onClick={() => openEdit(c)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="font-medium truncate">{c.name}</div><div className="text-[11px] text-muted-foreground truncate">{c.subject}</div></div>
                <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: statusColor[c.status], background: (statusColor[c.status] || "#94a3b8") + "22" }}>{t(`crm.comm.status.${c.status}`)}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {c.status === "programmee" && c.scheduled_at && <span className="inline-flex items-center gap-1 text-sky-300"><CalendarClock className="h-3 w-3" /> {new Date(c.scheduled_at).toLocaleString()}</span>}
                <span className="inline-flex items-center gap-1 text-emerald-300"><Send className="h-3 w-3" /> {s.delivered ?? 0}/{s.recipients ?? 0}</span>
                <span className="inline-flex items-center gap-1 text-violet-300"><MousePointerClick className="h-3 w-3" /> {s.clicks_unique ?? 0} ({s.click_rate ?? 0}%)</span>
                {(s.failed ?? 0) > 0 && <span className="inline-flex items-center gap-1 text-rose-300"><XCircle className="h-3 w-3" /> {s.failed}</span>}
                {(s.unsubscribed ?? 0) > 0 && <span className="inline-flex items-center gap-1 text-orange-300"><Mail className="h-3 w-3" /> {s.unsubscribed}</span>}
              </div>
              {!readOnly && <div className="mt-2 text-right" onClick={(e) => e.stopPropagation()}><Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => del(c)} data-testid={`comm-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button></div>}
            </div>
          );
        })}
        {!campaigns.length && <p className="text-muted-foreground text-sm col-span-full py-8 text-center">{t("crm.comm.empty")}</p>}
      </div>

      {/* Dialog campagne */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="h-5 w-5 text-emerald-400" /> {editId ? t("crm.comm.edit") : t("crm.comm.new")}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.comm.new")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Contenu */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-emerald-300 mb-2">{t("crm.comm.sectionContent")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("crm.comm.name")} <span className="text-rose-400">*</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="comm-name" /></div>
                <div className="space-y-1.5"><Label>{t("crm.comm.template")}</Label>
                  <Select value={form.template_id || "none"} onValueChange={(v) => v === "none" ? setForm({ ...form, template_id: "" }) : applyTemplate(v)}>
                    <SelectTrigger data-testid="comm-template"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{templates.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.comm.subject")} <span className="text-rose-400">*</span></Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="comm-subject" /></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.comm.body")} <span className="text-rose-400">*</span></Label><RichTextEditor value={form.body_html} onChange={(v) => setForm({ ...form, body_html: v })} placeholder={t("crm.comm.bodyPlaceholder")} testid="comm-body" /></div>
                <div className="space-y-1.5"><Label>{t("crm.comm.ctaLabel")}</Label><Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{t("crm.comm.ctaUrl")}</Label><Input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} placeholder="https://…" data-testid="comm-cta-url" /></div>
              </div>
            </div>

            {/* Segmentation */}
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-violet-300 mb-2 flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> {t("crm.comm.sectionSegment")}</p>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>{t("crm.comm.smartSegment")}</Label>
                  <Select value={form.segment.smart || "none"} onValueChange={(v) => setSeg({ smart: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="comm-smart"><SelectValue placeholder={t("crm.comm.smartNone")} /></SelectTrigger>
                    <SelectContent><SelectItem value="none">{t("crm.comm.smartNone")}</SelectItem>{SMART_SEGMENTS.map((x) => <SelectItem key={x.k} value={x.k}>{pl(lang, x)}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5"><Label>{t("crm.comm.categories")}</Label>
                  <div className="flex flex-wrap gap-2" data-testid="comm-categories">
                    {Object.entries(PARTNER_CATEGORIES).map(([k, v]) => (
                      <button key={k} type="button" onClick={() => toggleCat(k)} className="rounded-full px-3 py-1 text-xs border transition"
                        style={(form.segment.categories || []).includes(k) ? { borderColor: v.color, background: v.color + "22", color: v.color } : { borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>{pl(lang, v)}</button>
                    ))}
                  </div></div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.commune")}</Label><Input value={(form.segment.communes || []).join(", ")} onChange={(e) => setSeg({ communes: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Namur, Liège" /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.scoreMin")}</Label><Input type="number" min="0" max="100" value={form.segment.score_min ?? ""} onChange={(e) => setSeg({ score_min: e.target.value })} data-testid="comm-score-min" /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.inactiveDays")}</Label><Input type="number" min="0" value={form.segment.inactive_days_min ?? ""} onChange={(e) => setSeg({ inactive_days_min: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.satMin")}</Label><Input type="number" min="1" max="5" step="0.5" value={form.segment.satisfaction_min ?? ""} onChange={(e) => setSeg({ satisfaction_min: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.satMax")}</Label><Input type="number" min="1" max="5" step="0.5" value={form.segment.satisfaction_max ?? ""} onChange={(e) => setSeg({ satisfaction_max: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.orientMin")}</Label><Input type="number" min="0" value={form.segment.orientations_min ?? ""} onChange={(e) => setSeg({ orientations_min: e.target.value })} /></div>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={form.segment.include_optout} onCheckedChange={(v) => setSeg({ include_optout: !!v })} /> {t("crm.comm.includeOptout")}</label>
                {/* Aperçu destinataires */}
                <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.06] p-3 flex items-center justify-between" data-testid="comm-preview">
                  <span className="text-sm inline-flex items-center gap-2 text-emerald-200"><Users className="h-4 w-4" /> {t("crm.comm.eligible")}: <b>{preview?.eligible ?? "…"}</b></span>
                  <span className="text-[11px] text-muted-foreground">{t("crm.comm.previewDetail", { total: preview?.total ?? 0, email: preview?.with_email ?? 0, optout: preview?.optout ?? 0 })}</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            {editId && !readOnly && <Button variant="outline" onClick={() => { del({ id: editId }); setOpen(false); }} className="text-rose-400 mr-auto"><Trash2 className="h-4 w-4 mr-1" /> {t("crm.actions.delete")}</Button>}
            <div className="flex items-center gap-1">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder={t("crm.comm.testEmail")} className="h-9 w-44" data-testid="comm-test-email" />
              <Button variant="outline" onClick={sendTest} disabled={!editId} data-testid="comm-send-test"><Eye className="h-4 w-4 mr-1" /> {t("crm.comm.test")}</Button>
            </div>
            <Button variant="outline" onClick={save} data-testid="comm-save">{t("crm.comm.saveDraft")}</Button>
            <div className="flex items-center gap-1">
              <Input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className="h-9 w-48" data-testid="comm-sched-at" />
              <Button variant="outline" onClick={schedule} disabled={!editId} data-testid="comm-schedule"><CalendarClock className="h-4 w-4 mr-1" /> {t("crm.comm.schedule")}</Button>
            </div>
            {!readOnly && <Button onClick={sendNow} className="bg-emerald-600 hover:bg-emerald-500" data-testid="comm-send"><Send className="h-4 w-4 mr-1" /> {t("crm.comm.send")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog modèles */}
      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-400" /> {t("crm.comm.templatesTitle")}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.comm.templatesTitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {templates.length > 0 && (
              <div className="space-y-1.5">
                {templates.map((x) => (
                  <div key={x.id} className="flex items-center justify-between rounded-lg border border-white/10 p-2.5 bg-white/[0.02]" data-testid={`comm-tpl-${x.id}`}>
                    <span className="text-sm truncate">{x.name} <span className="text-[10px] text-muted-foreground">· {pl(lang, commTemplateEntry(x.template_type))}</span></span>
                    <span className="flex gap-1"><Button size="sm" variant="ghost" className="h-7" onClick={() => openTplEdit(x)}>{t("crm.actions.edit")}</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => delTpl(x)}><Trash2 className="h-3.5 w-3.5" /></Button></span>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-xl border border-white/10 p-3 space-y-3 bg-white/[0.02]">
              <p className="text-xs font-semibold text-emerald-300">{tplEditId ? t("crm.comm.tplEdit") : t("crm.comm.tplNew")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("crm.comm.tplName")}</Label><Input value={tpl.name} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} data-testid="comm-tpl-name" /></div>
                <div className="space-y-1.5"><Label>{t("crm.comm.tplType")}</Label>
                  <Select value={tpl.template_type} onValueChange={(v) => setTpl({ ...tpl, template_type: v })}>
                    <SelectTrigger data-testid="comm-tpl-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{COMM_TEMPLATE_TYPES.map((x) => <SelectItem key={x.k} value={x.k}>{pl(lang, x)}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.comm.subject")}</Label><Input value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} /></div>
                <div className="space-y-1.5 col-span-2"><Label>{t("crm.comm.body")}</Label><RichTextEditor value={tpl.body_html} onChange={(v) => setTpl({ ...tpl, body_html: v })} placeholder={t("crm.comm.bodyPlaceholder")} testid="comm-tpl-body" /></div>
              </div>
              <div className="text-right"><Button size="sm" onClick={saveTpl} className="bg-emerald-600 hover:bg-emerald-500" data-testid="comm-tpl-save">{t("crm.actions.save")}</Button></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog automatisations */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-amber-400" /> {t("crm.comm.auto.title")}</DialogTitle>
            <DialogDescription>{t("crm.comm.auto.desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {autos.length > 0 && (
              <div className="space-y-1.5" data-testid="comm-autos-list">
                {autos.map((a) => (
                  <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 p-2.5 bg-white/[0.02]" data-testid={`comm-auto-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate flex items-center gap-2">{a.name}
                        {!a.active && <span className="text-[10px] text-rose-300 rounded-full bg-rose-500/15 px-1.5">{t("crm.comm.auto.suspended")}</span>}</div>
                      <div className="text-[10px] text-muted-foreground">{pl(lang, automationTriggerEntry(a.trigger_type))} · {t("crm.comm.auto.totalSent", { count: a.total_sent })}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Switch checked={a.active} onCheckedChange={() => toggleAuto(a)} data-testid={`comm-auto-toggle-${a.id}`} />
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-400" onClick={() => runAuto(a)} title={t("crm.comm.auto.runNow")} data-testid={`comm-auto-run-${a.id}`}><Play className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="h-7" onClick={() => editAuto(a)}>{t("crm.actions.edit")}</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => delAuto(a)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="rounded-xl border border-white/10 p-3 space-y-3 bg-white/[0.02]">
              <p className="text-xs font-semibold text-amber-300">{autoEditId ? t("crm.comm.auto.edit") : t("crm.comm.auto.new")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t("crm.comm.auto.name")}</Label><Input value={autoForm.name} onChange={(e) => setAutoForm({ ...autoForm, name: e.target.value })} data-testid="comm-auto-name" /></div>
                <div className="space-y-1.5"><Label>{t("crm.comm.auto.trigger")}</Label>
                  <Select value={autoForm.trigger_type} onValueChange={(v) => setAutoForm({ ...autoForm, trigger_type: v, config: {} })}>
                    <SelectTrigger data-testid="comm-auto-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{AUTOMATION_TRIGGERS.map((x) => <SelectItem key={x.k} value={x.k}>{pl(lang, x)}</SelectItem>)}</SelectContent></Select></div>
              </div>
              {/* Config par déclencheur */}
              <div className="grid grid-cols-2 gap-3">
                {autoForm.trigger_type === "relance_inactivity" && (<>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.auto.inactiveDays")}</Label><Input type="number" min="1" value={autoForm.config.inactive_days ?? 60} onChange={(e) => setCfg({ inactive_days: Number(e.target.value) })} data-testid="comm-auto-inactive-days" /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.auto.cooldown")}</Label><Input type="number" min="1" value={autoForm.config.cooldown_days ?? 30} onChange={(e) => setCfg({ cooldown_days: Number(e.target.value) })} /></div>
                </>)}
                {autoForm.trigger_type === "voeux_annuels" && (<>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.auto.month")}</Label><Input type="number" min="1" max="12" value={autoForm.config.month ?? 1} onChange={(e) => setCfg({ month: Number(e.target.value) })} /></div>
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.auto.day")}</Label><Input type="number" min="1" max="31" value={autoForm.config.day ?? 1} onChange={(e) => setCfg({ day: Number(e.target.value) })} /></div>
                </>)}
                {autoForm.trigger_type === "rappel_visite" && (
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.auto.beforeDays")}</Label><Input type="number" min="0" value={autoForm.config.before_days ?? 2} onChange={(e) => setCfg({ before_days: Number(e.target.value) })} /></div>
                )}
                {autoForm.trigger_type === "relance_sans_clic" && (
                  <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.auto.afterDays")}</Label><Input type="number" min="0" value={autoForm.config.after_days ?? 3} onChange={(e) => setCfg({ after_days: Number(e.target.value) })} /></div>
                )}
              </div>
              <div className="space-y-1.5"><Label>{t("crm.comm.subject")}</Label><Input value={autoForm.subject} onChange={(e) => setAutoForm({ ...autoForm, subject: e.target.value })} data-testid="comm-auto-subject" /></div>
              <div className="space-y-1.5"><Label>{t("crm.comm.body")}</Label><Textarea rows={3} value={autoForm.body_html} onChange={(e) => setAutoForm({ ...autoForm, body_html: e.target.value })} data-testid="comm-auto-body" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.ctaLabel")}</Label><Input value={autoForm.cta_label} onChange={(e) => setAutoForm({ ...autoForm, cta_label: e.target.value })} /></div>
                <div className="space-y-1.5"><Label className="text-[11px]">{t("crm.comm.ctaUrl")}</Label><Input value={autoForm.cta_url} onChange={(e) => setAutoForm({ ...autoForm, cta_url: e.target.value })} /></div>
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs cursor-pointer"><Switch checked={autoForm.active} onCheckedChange={(v) => setAutoForm({ ...autoForm, active: v })} /> {t("crm.comm.auto.active")}</label>
                <Button size="sm" onClick={saveAuto} className="bg-amber-600 hover:bg-amber-500" data-testid="comm-auto-save">{t("crm.actions.save")}</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
